<template>
  <div class="daily-goal-view">
    <div class="daily-goal-title">{{ $t('DailyGoal.title.label') }}</div>
    <div class="daily-goal-description">
      {{ $t('DailyGoal.description.label') }}
    </div>
    <CircleProgress
      class="daily-progress mt-6 mb-2"
      :progress="currentDay.progress"
      :text="currentDayTime"
      :text-description="currentDayGoal"
      text-color="#0f172a"
      :size="160"
      clockwise
      :stroke-width="16"
      stroke-color="#fc6539"
      shade-color="#E2E8F0"
    />
    <BaseButton
      class="daily-goal-button mb-6"
      outlined
      color="#0f172a"
      @click="openDailyGoalPopup"
      >{{ $t('DailyGoal.button.label') }}</BaseButton
    >
    <div class="score-block mb-2">
      <div
        v-for="(item, index) in progress"
        :key="index"
        class="score-item"
        :class="{ active: item.index === selectedDay, today: item.today }"
        @click="showDayStatistic(item.index)"
      >
        <CircleProgress
          class="score-progress"
          :progress="item.progress"
          :text="item.text"
          :text-color="item.goal ? '#0f172a' : '#E2E8F0'"
          :size="36"
          clockwise
          :stroke-width="3.5"
          stroke-color="#fc6539"
          :shade-color="item.goal ? '#FFF0EB' : '#E2E8F0'"
        />
      </div>
    </div>
    <div class="streak-block">
      <BaseSpriteIcon custom-class="me-1" icon-name="ico-fire" />
      <span>{{ $t('DailyGoal.streak.message', { streak: myStreak }) }}</span>
    </div>
  </div>
</template>

<script>
import dayJS from '@/dayJS';
import { mapGetters } from 'vuex';
import PopupNamesEnum from '@/enums/PopupNamesEnum';
import AppConstantsUtil from '@/services/utils/AppConstantsUtil';
import CircleProgress from '@/components/base/CircleProgress/CircleProgress.vue';
import BaseButton from '@/components/base/BaseButton/BaseButton.vue';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon.vue';

export default {
  name: 'DailyGoal',
  components: { CircleProgress, BaseButton, BaseSpriteIcon },
  data() {
    return {
      selectedDay: null
    };
  },
  computed: {
    ...mapGetters('ContextStore', ['getSystemLanguage']),
    currentDay() {
      if (Number.isSafeInteger(this.selectedDay)) {
        return this.progress.find(p => p.index === this.selectedDay) || {};
      }
      const currentDayIndex = dayJS.getWeekday();
      return this.progress[currentDayIndex];
    },
    currentDayTime() {
      const minutes = Math.floor(this.currentDay.minutesRead);
      const seconds = Math.floor((this.currentDay.minutesRead - minutes) * 60);
      return dayJS.getDuration({ minutes, seconds }).format('m:ss');
    },
    currentDayGoal() {
      if (!this.currentDay.goal) {
        return this.$t('DailyGoal.todayProgress.noGoal');
      }
      return this.$t('DailyGoal.todayProgress.goal', {
        minutes: this.currentDay.goal
      });
    },
    myStreak() {
      return this.progress.reduce((result, day) => {
        const achievedGoal = day.goal && day.achievedGoal ? 1 : 0;
        return result + achievedGoal;
      }, 0);
    },
    progress() {
      const formattedWeekAgo = dayJS
        .get()
        .subtract(1, 'week')
        .format(AppConstantsUtil.USER_ACTIVITY_DATE_FORMAT);
      const weekDays = dayJS.getWeekdaysMin(this.getSystemLanguage); // system lang used for reactivity
      const activity =
        this.$store.getters['ActivityStore/getUserActivity'](
          formattedWeekAgo
        ) || [];
      const settings =
        this.$store.getters['ActivityStore/getUserActivitySettings'] || {};
      return weekDays.map(day => {
        const dayActivity =
          activity?.findLast?.(a => a.date === day.date) || null;
        let goal = dayActivity?.goal ?? 0;
        const progress = goal ? (dayActivity.minutesRead / goal) * 100 : 0;
        if (
          (day.timestamp >= settings.changedAt ||
            dayJS.isToday(day.timestamp)) &&
          settings.weekdays &&
          settings.weekdays.find(weekday => weekday.day === day.dayIndex)
        ) {
          goal = settings.goal;
        }
        return {
          goal,
          progress: Math.min(progress, 100),
          text: day.name,
          today: day.today,
          index: day.dayIndex,
          minutesRead: dayActivity?.minutesRead ?? 0,
          achievedGoal: dayActivity?.achievedGoal ?? false
        };
      });
    }
  },
  mounted() {
    this.showDayStatistic(this.currentDay.index);
  },
  methods: {
    openDailyGoalPopup() {
      this.$store.dispatch('ManagePopupStore/openPopup', {
        name: PopupNamesEnum.DAILY_GOAL_POPUP,
        popupContext: {}
      });
    },
    showDayStatistic(index) {
      this.selectedDay = index;
    }
  }
};
</script>

<style lang="less">
@import '~@/assets/styles/base.less';

.daily-goal-view {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;

  .daily-goal-title {
    font-weight: 700;
    font-size: 20px;
    line-height: 28px;
  }

  .daily-goal-description {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    opacity: 0.8;
  }

  .daily-progress {
    font-size: 24px;
    font-weight: 700;
  }

  .daily-goal-button {
    border: 1px solid #cbd5e1;
    border-radius: 8px;
  }

  .score-block {
    display: flex;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;
    max-width: 300px;

    @media @narrow {
      width: 100%;
      justify-content: space-between;
      gap: 4px;
    }

    .score-item {
      font-weight: 700;
      font-size: 14px;
      line-height: 20px;
      padding-bottom: 8px;
      cursor: pointer;

      &.today .progress-count {
        color: #fc6539;
      }

      &.active {
        box-shadow: inset 0 -3px 0 #fc6539;
      }
    }
  }

  .streak-block {
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    display: flex;
    align-items: center;
    color: #475569;

    svg {
      width: 16px;
      height: 16px;
      fill: #fc6539;
    }
  }
}
</style>
